/**
 * Utilitários de formatação numérica para o dashboard.
 * Todos os valores são formatados no locale pt-BR.
 */

/**
 * Formata score de maturidade (0-100) com até 1 casa decimal, omitindo
 * o ",0" quando o valor é inteiro.
 * Exemplos: 60.6141 → "60,6"; 72 → "72"; 88.0 → "88"
 */
export function formatScore(value: number): string {
  return value.toFixed(1).replace('.', ',').replace(/,0$/, '');
}

/**
 * Formata minutos como "X horas e Y minutos", arredondando ao inteiro.
 * Sempre exibe ambas as partes para evitar leitura ambígua de números altos.
 *
 * Exemplos:
 *   22  → "22 minutos"
 *   1   → "1 minuto"
 *   90  → "1 hora e 30 minutos"
 *   60  → "1 hora"
 *   120 → "2 horas"
 *   178 → "2 horas e 58 minutos"
 *   0   → "0 minutos"
 */
export function formatMinutes(value: number): string {
  const rounded = Math.round(value);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;

  if (hours === 0) {
    return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  }
  const hoursLabel = `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  if (minutes === 0) {
    return hoursLabel;
  }
  const minutesLabel = `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  return `${hoursLabel} e ${minutesLabel}`;
}

/**
 * Formata um total de horas fracionário como "X horas e Y minutos".
 * Delega em `formatMinutes` para padronizar o vocabulário.
 *
 * Exemplos: 8 → "8 horas", 8.5 → "8 horas e 30 minutos", 1.25 → "1 hora e 15 minutos"
 */
export function formatHours(value: number): string {
  return formatMinutes(value * 60);
}

/**
 * Formata percentagem com até 1 casa decimal, omitindo o ",0" quando inteiro.
 * Exemplos: 4.123 → "4,1%"; 20 → "20%"; 0 → "0%"
 */
export function formatPercentage(value: number): string {
  const formatted = value.toFixed(1).replace('.', ',').replace(/,0$/, '');
  return `${formatted}%`;
}

/**
 * Formata valor em BRL sem casas decimais.
 * Exemplo: 9703378 → "R$ 9.703.378"
 */
export function formatCurrencyBrl(value: number): string {
  const formatted = value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `R$ ${formatted}`;
}
